import { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { Banner, Button, Link, TruncateText } from '@panwds/react-ui'
import DropDown from '../common/DropDown/DropDown'
import {
  APP_ID_LOGGING_SERVICE,
  CREATE_NEW,
} from '../common/GlobalConstants'
import { ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP, ACTIVATION_UI_ERROR_CODES } from '../../ErrorCodes'
import { appIdsEligibleForActivation, isNonemptyArray } from '../common/utils'
import SubscriptionService from '../../services/SubscriptionService'
import UAccordionHeader from './UAccordionHeader'
import UTextBox from './UTextBox'
import ScreenLoader from '../../components/common/ScreenLoader'
import { updateActivationData, updateActivationInstances, updateStateProps } from '../../actions'

import type { VFC } from 'react'
import type { Instance } from '@sparky/framework'
import type { CommonStoreState, License, SubLicense } from '../../store'
import type { IDropDownData, IDropDownProps } from '../common/DropDown/DropDown'
import type { ManifestFlags } from '../../renderer'
import type { Actions } from '../../actions'
import type { BannerProps } from './UAccordion'
import { isDasCdlEnabled } from '../common/utils'
import { CDL_DEVICE_ASSOCIATIONS_MESSAGE, DEVICE_ASSOCIATIONS_LINK_FOLLOW_MESSAGE, DEVICE_ASSOCIATIONS_TEXT, DEVICE_ASSOCIATIONS_URL } from '../../constants'

const FIELD_NAME = 'cdl'
const SIZE_FIELD_NAME = 'cdlPurchasedSize'
const CDL_REGION_FIELD_NAME = `${APP_ID_LOGGING_SERVICE}-region`

const defaultProps = {
  title: 'Cortex Data Lake',
  subtitle: ''
}


type UCDLProps = {
  state: CommonStoreState
  actions: Actions
  cdlDropDownProps: IDropDownProps
  enableMigration?: boolean
  title?: string
  subtitle?: string
  updateTitle: (title: string) => void
  setDisable: (disable: boolean) => void
  setBanner?: (props: BannerProps) => void
  setAccordionOpen: (isOpen: boolean) => void
  completeAccordion: () => void
}

type cdlListProp = IDropDownData & {
  tenant_id: string
  need_migration: boolean
  purchased_size: number
  locked?: boolean
}

type SelectedCDL = {
  tenant_id: string,
  purchased_size: string,
}

const defaultSelectedCdl = {
  tenant_id: '',
  purchased_size: 'N/A',
}

const UCDL: VFC<UCDLProps> = (props) => {
  const { state, title, subtitle, actions, enableMigration, setBanner, setDisable, updateTitle, setAccordionOpen, completeAccordion, ...restProps } = props
  const { apps, licenses, instances, loadingTsg, manifest, globalTsgId, selectedPlatformRegion } = state
  const { setValue, setError, clearErrors } = useFormContext()

  const currentValuesRef = useRef<{ supportAccountId: string }>({ supportAccountId: '' })
  currentValuesRef.current.supportAccountId = state.supportAccountId

  const [cdlList, setCdlList] = useState<cdlListProp[]>([])
  // use this state to keep track of current selection CDL
  const [selectedCdl, setSelectedCdl] = useState<SelectedCDL>(defaultSelectedCdl)
  // if target TSG has CDL, it has to be locked
  const [isCdlLocked, setCdlLock] = useState<boolean>(false)
  const [allowModifyQty, setModifyQty] = useState<boolean>(true)
  const [isCSPCdlLoading, setIsCSPCdlLoading] = useState<boolean>(false)
  const [cspCdlMap, setCspCdlMap] = useState<{ [supportAccountId: string]: Instance[] }>({})
  const [availableCdl, setAvailableCdl] = useState<any[]>([])
  const [isDoneBtnEnabled, setDoneBtnEnabled] = useState(false)

  const currentTsgCdlInstance = useMemo(() => {
    return instances.get(APP_ID_LOGGING_SERVICE)
  }, [instances])

  const [cdlLicense, cdlSubLicense] = useMemo<[License | undefined, SubLicense | undefined]>(() => {
    const _cdlLicense = licenses?.get(APP_ID_LOGGING_SERVICE)
    return [_cdlLicense, _cdlLicense?.licenses[0]]
  }, [licenses])

  const createNew = useMemo(() => {
    return {
      value: CREATE_NEW,
      description: CREATE_NEW,
      children: CREATE_NEW,
      key: 'new',
      id: 'new',
      tenant_id: 'new',
      purchased_size: cdlSubLicense?.remained_size,
      disabled: false,
      need_migration: false,
      locked: false,
    }
  }, [cdlSubLicense])

  const isCdlLicenseAvailable = useMemo<boolean>(() => {
    return Boolean(cdlSubLicense && !cdlSubLicense.is_csp_activated)
  }, [cdlSubLicense])

  const minQuantity = useMemo<number>(() => {
    return currentTsgCdlInstance ? currentTsgCdlInstance?.extra?.purchased_size ?? 1 : 1
  }, [currentTsgCdlInstance])

  const maxQuantity = useMemo<number>(() => {
    const _remained_size = cdlSubLicense?.remained_size ?? 0
    if (selectedCdl.tenant_id === 'new') {
      // create new CDL, always use remained size as max
      return _remained_size
    }
    // use existing CDL, from TSG or CSP
    if (cdlLicense?.license_id && currentTsgCdlInstance?.tenant_id === selectedCdl.tenant_id && currentTsgCdlInstance?.csp_tenant_id === cdlLicense?.csp_tenant_id) {
      /**
       * activate from license, and current TSG has CDL, also is the same license source
       * then the max Quantity will be used plus remained
       */
      return (currentTsgCdlInstance.extra?.purchased_size ?? 0) + _remained_size
    }
    /**
     * rest cases will use the current CDL size as max quantity, whichs are:
     * 1. existing CDL from CSP for migration
     * 2. existing CDL from TSG, but license source is different, regardless activating from Hash or License
     */
    return cdlList?.find((_each) => _each.tenant_id === selectedCdl.tenant_id)?.purchased_size
  }, [cdlLicense, cdlSubLicense, selectedCdl, currentTsgCdlInstance, cdlList])

  const allocationType = useMemo<'FULL' | 'SHARED'>(() => {
    if (!cdlSubLicense) {
      // no CDL license present
      return 'FULL'
    }
    if (state.lockFullAllocation) {
      return 'FULL'
    }
    if (state.activationData?.managementMethod === 'onprem') {
      return 'FULL'
    }
    if (selectedCdl.tenant_id === 'new') {
      // new create CDL
      return +selectedCdl.purchased_size === +cdlSubLicense.purchased_size ? 'FULL' : 'SHARED'
    }
    if (cdlLicense?.license_id && cdlSubLicense?.allocated_size !== 0) {
      // has license_id (edit license or amend hash) and license already been used
      return 'SHARED'
    }
    return 'FULL'
  }, [cdlLicense?.license_id, cdlSubLicense, selectedCdl.purchased_size, selectedCdl.tenant_id, state.activationData?.managementMethod, state.lockFullAllocation])

  const appIdsToBeActivated = useMemo(() => {
    return appIdsEligibleForActivation(licenses, manifest?.primary_app_id as string, manifest?.flags as ManifestFlags)
  }, [licenses, manifest])

  const cdlQuantityValidator = useCallback((value) => {
    if (isNonemptyArray(cdlList)) {
      if (typeof setBanner === 'function') {
        setBanner({ display: (+value < minQuantity || +value > maxQuantity), message: 'Cdl Quantity Validation Error', appearance: 'error' })
      }
      return !(+value < minQuantity || +value > maxQuantity)
    }

    typeof setBanner === 'function' && setBanner({ display: false, message: '', appearance: 'error' })
    return true
  }, [cdlList, minQuantity, maxQuantity, setBanner])

  const writeCdlInstToActivationData = useCallback((selectedCdl: SelectedCDL, allocation_type: 'FULL' | 'SHARED') => {
    const _tenantId = selectedCdl.tenant_id
    updateActivationInstances({
      [APP_ID_LOGGING_SERVICE]: {
        app_id: APP_ID_LOGGING_SERVICE,
        activation_type: _tenantId === 'new' ? 'NEW' : 'AMEND',
        license_id: cdlLicense?.license_id,
        allocation_type,
        allocation: [
          {
            app_id: APP_ID_LOGGING_SERVICE,
            license_type: cdlSubLicense?.license_type ?? cdlList?.find(_each => _each.tenant_id === _tenantId)?.license_type ?? '',
            tenant_id: _tenantId,
            allocation_size: +selectedCdl.purchased_size
          }
        ]
      }
    })
  }, [cdlLicense?.license_id, cdlList, cdlSubLicense?.license_type])

  /**
   * write activation data when selectedCdl changed
   */
  useEffect(() => {
    writeCdlInstToActivationData(selectedCdl, allocationType)
    updateTitle(selectedCdl.purchased_size ? `Cortext Data Lake: ${selectedCdl.purchased_size} TB` : 'Cortext Data Lake')
    if (/^[0-9]*$/.test(selectedCdl?.purchased_size)) {
      setDoneBtnEnabled(true)
    }
    else {
      setDoneBtnEnabled(false)
    }
  }, [allocationType, selectedCdl, updateTitle, writeCdlInstToActivationData])

  useEffect(() => {
    if (loadingTsg) {
      return
    }
    clearErrors('cdl')
    if (state.tsg?.id && !currentTsgCdlInstance && manifest?.flags?.cdl_required && (!cdlSubLicense || cdlSubLicense?.is_csp_activated)) {
      setError('cdl', { message: ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.NO_ACCESS_TO_CDL] })
    }
    else if (currentTsgCdlInstance) {
      if (appIdsToBeActivated?.length === 1 && appIdsToBeActivated.includes(APP_ID_LOGGING_SERVICE)) {
        setError('cdl', { message: 'Invalid CDL' })
      }
    }
  }, [appIdsToBeActivated, cdlSubLicense, clearErrors, currentTsgCdlInstance, loadingTsg, manifest?.flags?.cdl_required, setError, state.tsg?.id])

  /**
   * API call to get CDL list, has cache to keep the csp already fetched
   * note: API error will set a empty list
   */
  const fetchCspCdl = useCallback(async (supportAccountId): Promise<Instance[]> => {
    setIsCSPCdlLoading(true)
    let _cspCdl: Instance[] = []
    if (cspCdlMap[supportAccountId]) {
      // if in map, skip making call
      _cspCdl = cspCdlMap[supportAccountId]
    }
    else {
      // if not in map, make API call
      const { data } = await SubscriptionService.instancesFromCSP.get(APP_ID_LOGGING_SERVICE, globalTsgId, supportAccountId).catch((err) => {
        return { data: [] }
      })
      if (currentValuesRef.current.supportAccountId === supportAccountId) {
        // skip if current one doesn't match the one made for API call
        _cspCdl = data.map((each: any) => {
          return {
            ...each,
            need_migration: true,
          }
        })
        setCspCdlMap((prevState) => {
          return {
            ...prevState,
            [supportAccountId]: _cspCdl
          }
        })
      }
    }
    setIsCSPCdlLoading(false)
    return _cspCdl
  }, [cspCdlMap, globalTsgId])

  /**
   * construct CDL dropdown items, based on Available Cdl list
   */
  useEffect(() => {
    // region will change, then effects the items
    const region = selectedPlatformRegion
    const items = availableCdl.map((each: any) => {
      return {
        // for migrated CDL, it's tenant_display_name, for instance store, it's tenant_instance_name
        value: `${each.tenant_display_name || each.tenant_instance_name || 'CDL'}`,
        children: `${each.tenant_display_name || each.tenant_instance_name || 'CDL'}`,
        description: 'existing instance',
        key: each.tenant_id,
        id: each.tenant_id,
        tenant_id: each.tenant_id,
        purchased_size: each?.extra?.purchased_size,
        disabled: region ? each.region !== region : false,
        need_migration: Boolean(each.need_migration),
        locked: Boolean(each.locked),
      }
    })
    if (isCdlLicenseAvailable) {
      items.unshift(createNew)
    }
    setCdlList(items)
  }, [availableCdl, createNew, isCdlLicenseAvailable, selectedPlatformRegion])

  /**
   * set Available Cdl list, based on TSG and CSP selection
   */
  useEffect(() => {
    if (loadingTsg) {
      return
    }
    (async () => {
      // fetchCspCdl based on selection
      let _availableCdl: Instance[]
      if ((state.tsg || state.tsgs[0].children?.length === 0) && state.supportAccountId) {
        setDisable(false)
        if (currentTsgCdlInstance) {
          // tsgInstances has CDL, that one has to be the only option
          // check license source of this currentTSGCdlInstance
          if (cdlLicense && !cdlLicense.license_id) {
            // hash activation, using existing CDL need to lock to FULL allocation
            updateStateProps({ lockFullAllocation: true })
          }
          else {
            updateStateProps({ lockFullAllocation: false })
          }
          _availableCdl = [
            {
              ...currentTsgCdlInstance,
              locked: true,
            } as Instance
          ]
          setCdlLock(true)
        }
        else {
          setCdlLock(false)
          if (enableMigration) {
            // no CDL inside TSG, need to fech based on CSP
            _availableCdl = await fetchCspCdl(state.supportAccountId)
          }
          else {
            _availableCdl = []
          }
        }
        setAvailableCdl(_availableCdl)
      }
      else {
        setDisable(true)
      }
    })()
  }, [cdlLicense, currentTsgCdlInstance, enableMigration, fetchCspCdl, loadingTsg, setDisable, state.supportAccountId, state.tsg, state.tsgs])

  /**
   * Set CDL Region Display
   */
  useEffect(() => {
    setValue(CDL_REGION_FIELD_NAME, apps?.get(APP_ID_LOGGING_SERVICE)?.regions?.find(each => each.name === selectedPlatformRegion)?.display ?? '')
  }, [apps, selectedPlatformRegion, setValue])

  /**
   * select CDL programmatically if CDL is locked
   * and reset if cdlList changed
   */
  /**
   * check if allow CDL modify qty based on managementMethod
   * APPORTAL-9660, this has to be put into same useEffect, as on amend, once panorama selection can be changed and no other field can change to trigger the correct setlock
   */
  useEffect(() => {
    if (state.activationData?.managementMethod === 'cloud') {
      setModifyQty(true)
    }
    if (state.activationData?.managementMethod === 'onprem') {
      setModifyQty(false)
    }
    if (isCdlLocked) {
      const lockedCdl = cdlList.find(each => each.locked)
      if (lockedCdl) {
        setSelectedCdl({
          tenant_id: lockedCdl?.tenant_id,
          purchased_size: lockedCdl?.purchased_size,
        })
        setValue(FIELD_NAME, lockedCdl)
        clearErrors(SIZE_FIELD_NAME)
      }
    }
    else {
      // if not locked, need to reset and ensure the accordion opened
      setValue(FIELD_NAME, null)
      setSelectedCdl(defaultSelectedCdl)
      setAccordionOpen(true)
    }
  }, [cdlList, clearErrors, isCdlLocked, setValue, state.activationData?.managementMethod, setAccordionOpen])

  const formatCdlDropDownValue = useCallback((item: any) => {
    return (
      <div
        className='tw-flex tw-flex-col'>
        <TruncateText>{item.value ?? item.key}</TruncateText>
      </div>
    )
  }, [])

  const onCdlChange = useCallback((selection: cdlListProp) => {
    setSelectedCdl({
      tenant_id: selection.tenant_id,
      purchased_size: selection.purchased_size,
    })
    updateStateProps({ lockFullAllocation: false })
    if (state.activationData?.managementMethod === 'cloud') {
      setModifyQty(true)
    }
    if (state.activationData?.managementMethod === 'onprem') {
      setModifyQty(false)
    }
    if (selection.need_migration) {
      // user picked migration CDL
      setValue(SIZE_FIELD_NAME, String(selection.purchased_size))
      setModifyQty(false)
      updateStateProps({ lockFullAllocation: true })
      updateActivationData({
        'migrate_csp_instance': {
          'tenant_ids': [selection.tenant_id]
        }
      })
    }
  }, [setValue, state.activationData?.managementMethod])

  const dropdown = useMemo(() => {
    return <DropDown
      {...{
        ...restProps.cdlDropDownProps,
        disabled: isCdlLocked,
        dropDownList: cdlList,
        isLoading: loadingTsg || isCSPCdlLoading,
        callbacks: {
          changeHandlerCallback: onCdlChange,
          formatOptionsCallback: formatCdlDropDownValue
        },
        fieldName: FIELD_NAME
      }} />
  }, [cdlList, formatCdlDropDownValue, isCSPCdlLoading, isCdlLocked, loadingTsg, onCdlChange, restProps.cdlDropDownProps])


  const updateCdlQuantity = useCallback((value) => {
    setSelectedCdl((prevState) => {
      return {
        ...prevState,
        purchased_size: value,
      }
    })
  }, [])

  const cdlQuantityComponent = useMemo(() => {
    return <UTextBox {...{
      value: selectedCdl.purchased_size,
      disabled: !allowModifyQty,
      onChangeHandler: updateCdlQuantity,
      placeholder: 'Enter CDL Quantity',
      name: SIZE_FIELD_NAME,
      showLabel: false,
      label: 'Data Log Storage',
      subTitle: `Up to ${maxQuantity ?? 0} ${cdlSubLicense?.purchased_unit ?? 'TB'} available`,
      errorMessage: 'CDL_QUANTITY_INPUT_ERROR',
      validationHandler: cdlQuantityValidator,
      link: {
        url: 'https://apps.paloaltonetworks.com/logging-service-calculator',
        text: 'Data log storage estimator',
        external: true,
      },
      height: '32px',
      width: '250px'
    }} />
  }, [selectedCdl.purchased_size, allowModifyQty, updateCdlQuantity, maxQuantity, cdlSubLicense?.purchased_unit, cdlQuantityValidator])

  const cdlRegionComponent = useMemo(() => {
    return <UTextBox {...{
      placeholder: 'CDL Region',
      name: CDL_REGION_FIELD_NAME,
      showLabel: false,
      label: 'CDL Region',
      subTitle: 'This is decided by your region selection',
      disabled: true,
      height: '32px',
      width: '250px'
    }} />
  }, [])

  const cdlDeviceAssociationsBanner = useMemo(() => {
    const linkMessage = <>Go to <Link href={DEVICE_ASSOCIATIONS_URL} target='_blank' rel="noreferrer">{DEVICE_ASSOCIATIONS_TEXT}</Link> {DEVICE_ASSOCIATIONS_LINK_FOLLOW_MESSAGE}</>
    const context = <span style={{ fontSize: '12px' }}>{CDL_DEVICE_ASSOCIATIONS_MESSAGE} {linkMessage} </span>
    return (
      <div className='cm-flex cm-flex-row' style={{ marginTop: '20px' }}>
        <div className='cm-flex cm-flex-col cm-pt-1 cm-flex-1'>
          <Banner showIcon> {context} </Banner>
        </div>
      </div>
    )
  }, [])

  return (
    <>
      {isCSPCdlLoading && <ScreenLoader />}
      {
        <div>
          <Button
            addClassName='cm-absolute cm-right-0 cm-top-0'
            appearance='primary'
            disabled={!isDoneBtnEnabled}
            onClick={() => {
              setAccordionOpen(false)
              completeAccordion()
            }}
          >Done</Button>
          <UAccordionHeader title={title || defaultProps.title} subtitle={subtitle || defaultProps.subtitle} />
          <div className='cm-flex cm-flex-row'>
            <div className='cm-flex-1'>
              {dropdown}
            </div>
            <div className='cm-flex cm-flex-col cm-pt-1  cm-flex-1'>
              {cdlQuantityComponent}
            </div>
            <div className='cm-flex cm-flex-col cm-pt-1 cm-flex-1'>
              {cdlRegionComponent}
            </div>
            {isDasCdlEnabled(manifest?.flags) && cdlDeviceAssociationsBanner}
          </div>
        </div>
      }
    </>
  )

}

export default UCDL
